import { ChangeEventHandler, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { connect } from 'scrivito'
import type { DataItem } from 'scrivito'

import './ContactSocialNetworkConnections.scss'
import { DeleteConnectionButton } from './DeleteConnectionButton'
import {
  getDefaultSocialNetworkUrl,
  getSocialNetworkIcon,
} from '../SocialNetwork/socialNetworkUtils'
import { BasicInput } from '@/Atomic/Atoms/Inputs/BasicInput'
import { Button } from '@/Atomic/Molecules/Button'
import {
  Sublist,
  SublistItem,
  SublistItemContent,
} from '@/Atomic/Templates/Sublist'
import { DataClassName } from '@/Data/const'
import { SocialNetwork } from '@/Data/SocialNetwork/SocialNetworkDataClass'
import { SchemaAttributes } from '@/Data/types'
import { useFormGroupHandler } from '@/hooks/useFormGroupHandler'
import { getSchemaName } from '@/utils/dataClassMap'
import { ensureString } from '@/utils/ensureString'
import { getSchema } from '@/utils/getSchema'
import { getLocalizedSchemaAttributes } from '@/utils/schemaLocalization'

interface ContactSocialNetworkConnectionProps {
  contactId: string
}

export const ContactSocialNetworkConnections: React.FC<ContactSocialNetworkConnectionProps> =
  connect(({ contactId }) => {
    const { t } = useTranslation()
    const formGroup = useFormGroupHandler()
    const [showAddForm, setShowAddForm] = useState(false)
    const [newUrl, setNewUrl] = useState('')
    const [urlError, setUrlError] = useState('')

    let socialNetworks: DataItem[] = []

    try {
      socialNetworks = SocialNetwork.all()
        .transform({
          filters: {
            contact: contactId,
          },
        })
        .take()
    } catch (error) {
      reportError(error)
    }

    const normalizeUrl = (url: string): string => {
      return url.startsWith('http') ? url : `https://${url}`
    }

    const validateSocialNetworkUrl = (
      url: string,
    ): 'noProfilePath' | 'invalidUrl' | null => {
      if (!url) return null

      try {
        const urlObj = new URL(url)
        const pathname = urlObj.pathname.replace(/\/$/, '') // Remove trailing slash

        // Check if URL only contains domain without profile path
        if (!pathname || pathname === '') {
          return 'noProfilePath'
        }

        return null
      } catch {
        return 'invalidUrl'
      }
    }

    const addNewSocialNetwork = () => {
      if (!newUrl) return

      const normalizedUrl = normalizeUrl(newUrl)
      const errorType = validateSocialNetworkUrl(normalizedUrl)
      if (errorType) {
        setUrlError(t(`components.socialNetwork.errors.${errorType}`))
        return
      }

      SocialNetwork.create({
        contact: contactId,
        url: normalizedUrl,
      }).then(() => {
        setShowAddForm(false)
        setNewUrl('')
        setUrlError('')
      })
    }

    const handleUrlChange: ChangeEventHandler<HTMLInputElement> = ({
      target: { value },
    }) => {
      setNewUrl(value)
      if (urlError) {
        setUrlError('') // Clear error on input change
      }
    }

    useEffect(() => {
      if (!formGroup.isEditing) {
        setShowAddForm(false)
        setNewUrl('')
        setUrlError('')
      }
    }, [formGroup.isEditing])

    const { schema } = getSchema(getSchemaName(DataClassName.SOCIAL_NETWORK))
    const schemaAttributes = getLocalizedSchemaAttributes(
      schema?.get('attributes') as SchemaAttributes,
    )

    const getNetworkName = (networkId: string) => {
      return (
        schemaAttributes?.network?.enumValues?.find(
          (opt) => opt.value === networkId,
        )?.title || networkId
      )
    }

    return (
      <>
        <h5 className="card-headline h6">
          {t('components.socialNetwork.socialNetwork')}
        </h5>
        <div className="card">
          {socialNetworks.length > 0 ? (
            <Sublist className="contact-social-network-connections__sublist">
              {socialNetworks.map((dataItem) => {
                const networkId = ensureString(dataItem.get('network'))
                const profile = ensureString(dataItem.get('profile'))
                const url = ensureString(dataItem.get('url'))
                const iconName = getSocialNetworkIcon(networkId)
                const networkName = getNetworkName(networkId)
                const targetUrl = url || getDefaultSocialNetworkUrl(networkId)

                const handleClick = () => {
                  if (targetUrl && targetUrl !== '#') {
                    window.open(targetUrl, '_blank', 'noopener,noreferrer')
                  }
                }

                return (
                  <SublistItem key={dataItem.id()}>
                    <SublistItemContent
                      avatarIcon={iconName}
                      title={networkName}
                      subtitle={profile ? `@${profile}` : url}
                      onClick={handleClick}
                      showButton="onHover"
                      button={
                        <DeleteConnectionButton
                          dataItem={dataItem}
                          title={t(
                            'components.socialNetwork.modal.delete.title',
                          )}
                          subtitle={t(
                            'components.socialNetwork.modal.delete.subtitle',
                          )}
                        />
                      }
                    />
                  </SublistItem>
                )
              })}
            </Sublist>
          ) : (
            !formGroup.isEditing && (
              <div className="card-body readonly-mode">
                <form className="readonly-mode">
                  <div className="row g-3">
                    <div className="col-lg">
                      <div className="mb-2 col"></div>
                    </div>
                  </div>
                </form>
              </div>
            )
          )}

          <div className="card-footer readonly-mode">
            {!showAddForm ? (
              <Button
                type="button"
                size="sm"
                variant="outlined"
                icon="add"
                buttonClassName="m-0"
                onClick={() => setShowAddForm(true)}
              >
                {t('components.socialNetwork.add')}
              </Button>
            ) : (
              <div className="mt-2 mb-2">
                <div className="d-flex">
                  <BasicInput
                    name="social-network-url"
                    type="text"
                    value={newUrl}
                    onChange={handleUrlChange}
                    placeholder={t('components.socialNetwork.urlPlaceholder')}
                    inputWrapperClassName="flex-grow-1"
                  />
                  <Button
                    variant="outlined"
                    buttonClassName="px-4 ms-2"
                    onClick={addNewSocialNetwork}
                  >
                    {t('common.add')}
                  </Button>
                </div>
                {urlError && (
                  <div
                    className="text-danger small mt-1"
                    role="alert"
                    aria-live="polite"
                  >
                    {urlError}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </>
    )
  })
